/**
 * CSRF (Cross-Site Request Forgery) Protection Utilities
 * 
 * Purpose: Prevent unauthorized actions from being performed on behalf of authenticated users
 * Threats mitigated:
 * - CSRF attacks where malicious sites make requests to your API
 * - Unauthorized state-changing operations
 * 
 * Note: Full CSRF protection requires server-side implementation.
 * This provides client-side token management.
 */

import { logError } from '@/utils/logger'

const CSRF_TOKEN_KEY = 'csrf_token'

/**
 * Generates a CSRF token (client-side only - server should validate)
 */
export function generateCsrfToken(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Stores CSRF token securely
 */
export function setCsrfToken(token: string): void {
  try {
    // Store in sessionStorage (cleared on tab close)
    sessionStorage.setItem(CSRF_TOKEN_KEY, token)
  } catch (error) {
    logError('Failed to store CSRF token', error)
  }
}

/**
 * Retrieves CSRF token
 */
export function getCsrfToken(): string | null {
  try {
    return sessionStorage.getItem(CSRF_TOKEN_KEY)
  } catch {
    return null
  }
}

/**
 * Validates CSRF token format
 */
export function isValidCsrfToken(token: string | null): boolean {
  if (!token || typeof token !== 'string') {
    return false
  }
  // CSRF tokens should be alphanumeric and of reasonable length
  return /^[a-f0-9]{64}$/i.test(token)
}

/**
 * Initializes CSRF protection (call on app startup)
 */
export function initCsrfProtection(): string {
  let token = getCsrfToken()
  if (!token || !isValidCsrfToken(token)) {
    token = generateCsrfToken()
    setCsrfToken(token)
  }
  return token
}

